import React, { PureComponent } from 'react';
import PropTypes from 'prop-types';
import * as classes from './classes.module.scss';
import Work from '../work';
import Draggable from '../shared/draggable';
import DraggingObject from '../shared/drag_object';
import Meter from './meter';
import WorkingArea from './working_area';
import Silhouette from './silhouette';
import WhiteBg from './white_bg';
import CustomImage from './custom_image';
import { HRuler, VRuler } from '../ruler';
import Guides from '../guides';
import CustomCrop from '../custom_crop';
import { calculateCanvasSize } from '../../config/utils';

const renderOptimial = () => (window.wbOptimalWarning ? (
  <section>
    For optimal results, use the wall designer via your computer (and not a tablet)
  </section>
) : null);

const renderWelcome = () => (
  <div className={ classes.welcome }>
    <h2>Curate and Design Your Walls</h2>
    <h3>
      Start by clicking the &quot;Select Art&quot; button in the upper left corner of the toolbar
    </h3>
    { renderOptimial() }
  </div>
);

export default class Canvas extends PureComponent {
  static propTypes = {
    currentColor: PropTypes.string.isRequired,
    dpi: PropTypes.number.isRequired,
    works: PropTypes.array.isRequired,
    moveWorkTo: PropTypes.func.isRequired,
    showWorkDetails: PropTypes.func.isRequired,
    setCenter: PropTypes.func.isRequired,
    recalculateDpis: PropTypes.func.isRequired,
    posX: PropTypes.number.isRequired,
    posY: PropTypes.number.isRequired,
    centerX: PropTypes.number.isRequired,
    centerY: PropTypes.number.isRequired,
    rulerVisible: PropTypes.bool.isRequired,
    silhouette: PropTypes.string.isRequired,
    showSilhouette: PropTypes.bool.isRequired,
    silhouetteX: PropTypes.number.isRequired,
    silhouetteY: PropTypes.number.isRequired,
    customImage: PropTypes.object,
    moveCustomImage: PropTypes.func.isRequired,
    resetCenter: PropTypes.func.isRequired,
    wallPaper: PropTypes.object
  };

  constructor(props) {
    super(props);
    const { cWidth, cHeight } = calculateCanvasSize();
    this.state = { w: cWidth, h: cHeight };
    this.workingArea = React.createRef();
  }

  onWorkClicked = (index) => {
    const { showWorkDetails } = this.props;
    showWorkDetails(index);
  }

  moveWorkTo = ({ x, y, index }) => {
    const { moveWorkTo } = this.props;
    const { x: newX, y: newY } = this.pixelsToInches({ x, y });
    moveWorkTo({ x: newX, y: newY, index });
  }

  moveCustomImageTo = ({ x, y }) => {
    const { moveCustomImage } = this.props;
    const { x: newX, y: newY } = this.pixelsToInches({ x, y });
    moveCustomImage({ posX: newX, posY: newY });
  };

  pixelsToInches = ({ x, y }) => {
    const { dpi } = this.props;
    return {
      x: x / dpi,
      y: y / dpi
    };
  }

  inchesToPixels({ x, y }) {
    const { dpi } = this.props;
    return {
      x: x * dpi,
      y: y * dpi
    };
  }

  renderWork = (draggingOpts, work, index) => {
    const { dpi } = this.props;
    const position = this.inchesToPixels(work);
    return (
      <DraggingObject
        className={ classes.work }
        objectId={ `work-${ index }` }
        onClick={ this.onWorkClicked }
        { ...draggingOpts }
        { ...position }
        key={ index }
        index={ index }
        dpi={ dpi }
      >
        {
          ({ x, y, dragging }) => (
            <Work
              { ...work }
              index={ index }
              x={ x }
              y={ y }
              moveWorkTo={ this.moveWorkTo }
              onClick={ this.onWorkClicked }
              dpi={ dpi }
              dragging={ dragging }
            />
          )
        }
      </DraggingObject>
    );
  }

  renderSilhouette() {
    const {
      silhouetteX, silhouetteY, showSilhouette, dpi, silhouette, posX, posY, centerX, centerY
    } = this.props;
    if (!showSilhouette) {
      return null;
    }
    const position = this.inchesToPixels({ x: silhouetteX, y: silhouetteY });
    const bgProps = {
      posX, posY, centerX, centerY, dpi, silhouetteY
    };
    return (
      <>
        <Silhouette
          dpi={ dpi }
          { ...position }
          silhouette={ silhouette }
        />
        <WhiteBg { ...bgProps } />
      </>
    );
  }

  renderCustomImage(draggingOpts) {
    const {
      dpi,
      customImage
    } = this.props;
    const {
      src, posX, posY, widthIn, heightIn
    } = customImage || {};
    if (!src) {
      return null;
    }
    const position = this.inchesToPixels({ x: posX, y: posY });
    return (
      <DraggingObject
        className={ classes.customImage }
        objectId="customImage"
        { ...draggingOpts }
        { ...position }
        limitNegative
      >
        {
          ({ x, y, dragging }) => (
            <CustomImage
              x={ x }
              src={ src }
              y={ y }
              dpi={ dpi }
              widthIn={ widthIn }
              heightIn={ heightIn }
              dragging={ dragging }
              moveCustomImageTo={ this.moveCustomImageTo }
            />
          )
        }
      </DraggingObject>
    );
  }

  render() {
    const {
      works, dpi, setCenter, posX, posY, currentColor, wallPaper, rulerVisible,
      resetCenter
    } = this.props;

    if (!works.length) {
      return renderWelcome();
    }

    const { x: posX0, y: posY0 } = this.inchesToPixels({ x: posX, y: posY });
    const { w, h } = this.state;
    const canvasId = 'canvas';
    const style = { backgroundColor: currentColor };
    if (wallPaper && wallPaper.src) {
      style.backgroundImage = `url(${ wallPaper.src })`;
      const { x: bgW, y: bgH } = this.inchesToPixels({
        x: wallPaper.widthIn, y: wallPaper.heightIn
      });
      style.backgroundSize = `${ bgW }px ${ bgH }px`;
      style.backgroundRepeat = 'repeat';
    }

    const rulers = rulerVisible ? (
      <>
        <HRuler />
        <VRuler />
      </>
    ) : null;

    return (
      <Draggable
        className={ classes.workingArea }
        defaultObjectId={ canvasId }
        childRef={ this.workingArea }
        style={ style }
      >
        {
          ({ defaultChild, ...draggingOpts }) => (
            <>
              { rulers }
              <Guides />
              <DraggingObject
                className={ classes.canvas }
                objectId={ canvasId }
                { ...draggingOpts }
                x={ -posX0 }
                y={ -posY0 }
                ref={ defaultChild }
              >
                {
                  ({ x, y }) => (
                    <>
                      <CustomCrop />
                      <WorkingArea
                        x={ x }
                        y={ y }
                        w={ w }
                        h={ h }
                        dpi={ dpi }
                        works={ works }
                        draggingOpts={ draggingOpts }
                        pixelsToInches={ this.pixelsToInches }
                        setCenter={ setCenter }
                        renderWork={ this.renderWork }
                        resetCenter={ resetCenter }
                      />
                      { this.renderSilhouette() }
                      { this.renderCustomImage(draggingOpts) }
                    </>
                  )
                }
              </DraggingObject>
              <Meter dpi={ dpi } />
            </>
          )
        }
      </Draggable>
    );
  }
}
